import React, { useState } from "react";
import { ChevronDown, Coins, Loader2, Menu } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import MapleOSLogo from "@/components/common/MapleOSLogo";
import UserActions from "@/components/layout/UserActions";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { useAICredits } from "@/hooks/useAICredits";

type NavItem = {
  label: string;
  to: string;
  external?: boolean;
};

const exploreLinks: NavItem[] = [
  { label: "Welcome", to: "/#hero-slider" },
  { label: "Living System", to: "/#a-living-system" },
  { label: "Try MapleOS", to: "/#try-mapleos" },
  { label: "Builders", to: "/#built-for-builders" },
  { label: "App Ecosystem", to: "/#current-app-ecosystem" },
];

const productLinks: NavItem[] = [
  { label: "MapleOS 26", to: "/products/mapleos-26" },
  { label: "MapleOS Pro 26", to: "/products/mapleos-pro-26" },
  { label: "MapleOS Enterprise 26", to: "/products/mapleos-enterprise-26" },
];

const communityLinks: NavItem[] = [
  { label: "News", to: "/news" },
  { label: "Forums", to: "/forums" },
  { label: "Bugs", to: "/bugs" },
  { label: "Partnership", to: "/partnership" },
  { label: "GitHub", to: "https://github.com/MapleOSAI", external: true },
];

type LandingNavbarProps = {
  showSectionLinks?: boolean;
};

type DropdownNavProps = {
  label: string;
  items: NavItem[];
};

type MobileNavSectionProps = {
  title: string;
  items: NavItem[];
};

function DropdownNav({ label, items }: DropdownNavProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 whitespace-nowrap text-[13px] text-white/65 outline-none ring-0 transition-colors duration-200 hover:text-white focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 xl:text-sm"
        >
          {label}
          <ChevronDown className="h-4 w-4 text-white/45" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        alignOffset={-6}
        sideOffset={10}
        className="min-w-[11.5rem] w-auto rounded-2xl border-white/10 bg-[linear-gradient(180deg,rgba(12,18,28,0.98),rgba(7,12,20,0.98))] p-1 text-white shadow-[0_24px_80px_rgba(0,0,0,0.42)]"
      >
        {items.map((item) => (
          <DropdownMenuItem
            key={item.label}
            asChild
            className="rounded-xl px-2.5 py-1 text-white/78 focus:bg-white/10 focus:text-white"
          >
            {item.external ? (
              <a
                href={item.to}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full whitespace-nowrap text-right font-medium text-white"
              >
                {item.label}
              </a>
            ) : (
              <Link to={item.to} className="block w-full whitespace-nowrap text-right font-medium text-white">
                {item.label}
              </Link>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileNavSection({ title, items }: MobileNavSectionProps) {
  return (
    <div className="space-y-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-white/45">{title}</div>
      <div className="grid gap-2">
        {items.map((item) => (
          <SheetClose asChild key={item.label}>
            {item.external ? (
              <a
                href={item.to}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-xl px-3 py-2 text-sm font-medium text-white/80 transition-colors hover:bg-white/8 hover:text-white"
              >
                {item.label}
              </a>
            ) : (
              <Link
                to={item.to}
                className="rounded-xl px-3 py-2 text-sm font-medium text-white/80 transition-colors hover:bg-white/8 hover:text-white"
              >
                {item.label}
              </Link>
            )}
          </SheetClose>
        ))}
      </div>
    </div>
  );
}

export default function LandingNavbar({ showSectionLinks = true }: LandingNavbarProps) {
  const { user } = useAuth();
  const { account, loading } = useAICredits();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <motion.nav
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: "easeOut" }}
      className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-background/80 backdrop-blur-xl"
    >
      <div className="mx-auto max-w-7xl px-4 py-3 sm:px-6 lg:py-3 xl:py-4">
        <div className="flex items-center justify-between gap-3 lg:hidden">
          <Link to="/" className="group flex min-w-0 shrink items-center gap-2" aria-label="mapleos home">
            <MapleOSLogo className="h-8 w-auto max-w-[10.5rem] text-foreground transition-colors duration-300 group-hover:text-primary" />
          </Link>

          <div className="flex shrink-0 items-center gap-2">
            {user ? <UserActions variant="landing" /> : <UserActions variant="landing" className="text-sm text-white/70 transition-colors hover:text-white" />}

            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white transition-colors hover:bg-white/10"
                  aria-label="Open navigation menu"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="h-dvh overflow-y-auto overscroll-y-contain w-[88vw] max-w-sm border-white/10 bg-[linear-gradient(180deg,rgba(12,18,28,0.98),rgba(7,12,20,0.98))] text-white"
              >
                <SheetHeader className="space-y-1 text-left">
                  <SheetTitle className="text-white">MapleOS</SheetTitle>
                  <SheetDescription className="text-white/60">
                    Browse public pages, downloads, and community links.
                  </SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-4 pb-6">

                  {user ? (
                    <SheetClose asChild>
                      <Link
                        to="/credits"
                        className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
                      >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Coins className="h-4 w-4" />}
                        <span>Credits</span>
                        <span className="text-white/70">{account.availableCredits.toLocaleString()}</span>
                      </Link>
                    </SheetClose>
                  ) : null}

                  <div className="grid gap-3 sm:grid-cols-2">
                    <SheetClose asChild>
                      <Link
                        to="/downloads"
                        className="inline-flex w-full items-center justify-center rounded-full border border-white/10 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/5"
                      >
                        Download OS
                      </Link>
                    </SheetClose>
                    <SheetClose asChild>
                      <Link
                        to="/start"
                        className="inline-flex w-full items-center justify-center rounded-full bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-[0_0_24px_hsl(var(--primary)/0.25)] transition-transform duration-200 hover:scale-[1.01] active:scale-[0.98]"
                      >
                        Launch mapleos
                      </Link>
                    </SheetClose>
                  </div>

                  {showSectionLinks ? (
                    <>
                      <MobileNavSection title="Explore" items={exploreLinks} />
                      <MobileNavSection title="Products" items={productLinks} />
                      <MobileNavSection title="Community" items={communityLinks} />
                      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-2">
                        <SheetClose asChild>
                          <Link
                            to="/docs"
                            className="block rounded-xl px-3 py-2 text-sm font-medium text-white/80 transition-colors hover:bg-white/8 hover:text-white"
                          >
                            Docs
                          </Link>
                        </SheetClose>
                      </div>
                    </>
                  ) : null}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2 lg:hidden">
          <Link
            to="/downloads"
            className="inline-flex min-w-0 items-center justify-center rounded-full border border-white/10 px-3 py-2 text-center text-sm font-semibold text-white transition-colors duration-200 hover:bg-white/5"
          >
            Download OS
          </Link>
          <Link
            to="/start"
            className="inline-flex min-w-0 items-center justify-center rounded-full bg-primary px-3 py-2 text-center text-sm font-semibold text-primary-foreground shadow-[0_0_24px_hsl(var(--primary)/0.25)] transition-transform duration-200 hover:scale-[1.01] active:scale-[0.98]"
          >
            Launch mapleos
          </Link>
        </div>

        <div className="hidden items-center justify-between gap-x-4 gap-y-3 lg:flex xl:gap-x-6">
          <Link to="/" className="group flex shrink-0 items-center gap-2" aria-label="mapleos home">
            <MapleOSLogo className="h-7 w-auto text-foreground transition-colors duration-300 group-hover:text-primary xl:h-8" />
          </Link>

          {showSectionLinks ? (
            <div className="flex min-w-0 flex-1 items-center justify-center gap-x-4 px-4 text-[13px] text-white/65 xl:gap-x-6 xl:text-sm">
              <DropdownNav label="Explore" items={exploreLinks} />
              <DropdownNav label="Products" items={productLinks} />
              <DropdownNav label="Community" items={communityLinks} />
              <Link to="/docs" className="whitespace-nowrap transition-colors duration-200 hover:text-white">
                Docs
              </Link>
            </div>
          ) : null}

          <div className="ml-auto flex shrink-0 items-center justify-end gap-2 xl:gap-3">
            {user ? (
              <>
                <Link
                  to="/credits"
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-[13px] font-semibold text-white transition-colors duration-200 hover:bg-white/10 xl:px-4 xl:text-sm"
                  title="View and buy AI credits"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Coins className="h-4 w-4" />}
                  <span className="hidden xl:inline">Credits</span>
                  <span className="text-white/70">{account.availableCredits.toLocaleString()}</span>
                </Link>
                <UserActions variant="landing" />
              </>
            ) : (
              <UserActions variant="landing" className="text-[13px] text-white/70 transition-colors hover:text-white xl:text-sm" />
            )}

            <Link
              to="/downloads"
              className="inline-flex rounded-full border border-white/10 px-3 py-2 text-[13px] font-semibold text-white transition-colors duration-200 hover:bg-white/5 xl:px-4 xl:text-sm"
            >
              <span className="xl:hidden">Download</span>
              <span className="hidden xl:inline">Download OS</span>
            </Link>
            <Link
              to="/start"
              className="inline-flex rounded-full bg-primary px-3 py-2 text-[13px] font-semibold text-primary-foreground shadow-[0_0_24px_hsl(var(--primary)/0.25)] transition-transform duration-200 hover:scale-[1.03] active:scale-[0.97] xl:px-4 xl:text-sm"
            >
              <span className="xl:hidden">Launch</span>
              <span className="hidden xl:inline">Launch mapleos</span>
            </Link>
          </div>
        </div>
      </div>
    </motion.nav>
  );
}